import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, BUSINESS_TYPES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { listClientDocs, uploadClientDoc, publicDocUrl, resolveDocSrc } from '../../storage';
import { createAuthFor, syncAuthPassword } from '../../api';
import { generateTempPassword } from '../../core/helpers';
import { Icon } from '../../components/Icon';


function EditClientModal({ data, onClose }) {
  const { clients, setClients, employees, showToast, user, users, userLinks, setUserLinks, runSubmitting } = useApp();
  const client = clients.find(c => c.id === data?.clientId);
  const [form, setForm] = useState({
    name:        client?.name || "",
    contactName: client?.contactName || "",
    email:       client?.email || "",
    phone:       client?.phone || "",
    type:        client?.type || "Private Limited",
    status:      client?.status || "active",
    assignedTo:  client?.assignedTo || "",
    gst:         client?.gst || "",
    pan:         client?.pan || "",
    state:       client?.state || "",
    incorporationDate: client?.incorporationDate || "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!client) return null;

  // #12 Directors linked to this company
  const linkedDirIds = (userLinks || []).filter(l => l.company_id === client.id).map(l => l.user_id);
  const allDirectors = (Array.isArray(users) ? users : []).filter(u => u.role === "client" || u.role === "director");
  const addableDirs  = allDirectors.filter(d => !linkedDirIds.includes(d.id));
  const dirName = (id) => allDirectors.find(d => d.id === id)?.name || "Director";
  const addDir = async (did) => {
    if (!did) return;
    try { await sb.insert("user_companies", { user_id: did, company_id: client.id }); } catch {}
    setUserLinks(ls => [...ls, { user_id: did, company_id: client.id }]);
  };
  const removeDir = async (did) => {
    try { await sb.delete("user_companies", `user_id=eq.${did}&company_id=eq.${client.id}`); } catch {}
    setUserLinks(ls => ls.filter(l => !(l.user_id===did && l.company_id===client.id)));
  };

  const save = async () => {
    if (!form.name) { showToast("Client name required", "error"); return; }
    // #G2 Format validation for statutory identifiers
    if (form.pan && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(form.pan.trim())) { showToast("PAN looks wrong — format AABCF1234A", "error"); return; }
    if (form.gst && form.gst.replace(/\s/g,"").length !== 15) { showToast("GSTIN should be 15 characters", "error"); return; }
    if (form.cin && form.cin.replace(/\s/g,"").length !== 21) { showToast("CIN should be 21 characters", "error"); return; }

    await runSubmitting('Saving client…', async () => {
    const result = await sb.update("companies", "id", client.id, {
      name:         form.name,
      contact_name: form.contactName,
      email:        form.email,
      phone:        form.phone,
      type:         form.type,
      status:       form.status,
      assigned_to:  form.assignedTo || null,
      gst:          form.gst || null,
      pan:          form.pan || null,
      state:        form.state || null,
      incorporation_date: form.incorporationDate || null,
    });

    // Always update user record for client login
    await sb.update("users", "id", client.id, {
      email: form.email?.toLowerCase(),
      phone: form.phone,
      name:  form.contactName || form.name,
    });

    // Update local state
    setClients(cs => cs.map(c => c.id === client.id ? {
      ...c, ...form,
    } : c));

    showToast("Client updated successfully!", "success");
    onClose();
    }, 'client');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 560 }}>
      <div className="modal-head">
        <div className="modal-title">Edit Client — {client.name}</div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="modal-body">
        <div className="form-grid-2">
          <div className="f-group">
            <label className="f-label">Company Name <span className="f-req">*</span></label>
            <input className="f-input" value={form.name} onChange={e=>set("name",e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Contact Person</label>
            <input className="f-input" value={form.contactName} onChange={e=>set("contactName",e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Email</label>
            <input className="f-input" type="email" value={form.email} onChange={e=>set("email",e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Phone</label>
            <input className="f-input" value={form.phone} onChange={e=>set("phone",e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Company Type</label>
            <select className="f-select" value={form.type} onChange={e=>set("type",e.target.value)}>
              {BUSINESS_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Status</label>
            <select className="f-select" value={form.status} onChange={e=>set("status",e.target.value)}>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Date of Incorporation / Registration</label>
            <input className="f-input" type="date" value={form.incorporationDate} onChange={e=>set("incorporationDate",e.target.value)} />
            <div className="f-hint">Used to schedule one-time deadlines (INC-20A, ADT-1, first board meeting).</div>
          </div>
          <div className="f-group">
            <label className="f-label">Assigned To</label>
            <select className="f-select" value={form.assignedTo} onChange={e=>set("assignedTo",e.target.value)}>
              <option value="">Unassigned</option>
              {employees.map(e=><option key={e.id} value={e.id}>{e.name} ({e.role})</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">State</label>
            <select className="f-select" value={form.state||""} onChange={e=>set("state",e.target.value)}>
              <option value="">Select state…</option>
              {["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Chandigarh"].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">CIN / LLP Number</label>
            <input className="f-input" value={form.cin||""} onChange={e=>set("cin",e.target.value)} placeholder="e.g. U74999MH2020PTC123456"/>
          </div>
          <div className="f-group">
            <label className="f-label">GST Number</label>
            <input className="f-input" value={form.gst} onChange={e=>set("gst",e.target.value)} placeholder="27AABCF1234A1Z5" />
          </div>
          <div className="f-group">
            <label className="f-label">PAN Number</label>
            <input className="f-input" value={form.pan} onChange={e=>set("pan",e.target.value)} placeholder="AABCF1234A" />
          </div>
          <div className="f-group" style={{ gridColumn:"1/-1" }}>
            <label className="f-label">Portal logins linked to this company</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
              {linkedDirIds.length === 0 && <span style={{ fontSize:12, color:"var(--muted)" }}>No portal logins linked — client will see an empty portal until you add one below.</span>}
              {linkedDirIds.map(did => (
                <span key={did} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#EEF2FF", border:"1px solid #C7D2FE", borderRadius:20, padding:"3px 10px", fontSize:12 }}>
                  {dirName(did)}
                  <span style={{ cursor:"pointer", color:"#B91C1C", fontWeight:700 }} title="Remove" onClick={()=>removeDir(did)}>×</span>
                </span>
              ))}
            </div>
            <select className="f-select" value="" onChange={e=>{ addDir(e.target.value); e.target.value=""; }}>
              <option value="">+ Link a portal login…</option>
              {addableDirs.map(d => <option key={d.id} value={d.id}>{d.name} ({d.email||d.phone})</option>)}
            </select>
            <div className="f-hint">Link client portal logins (Team → Directors) to this company. Invoices and tasks appear only after linking.</div>
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Save Changes</button>
      </div>
    </div>
  );
}


export { EditClientModal };
